import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { DuplicateIconButton } from '../../components/ui/DuplicateIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ViewIconLink } from '../../components/ui/ViewIconLink';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  EvaluationCycle,
  EvaluationCycleUpsertPayload,
  createEvaluationCycle,
  deleteEvaluationCycle,
  duplicateEvaluationCycle,
  listEvaluationCycles,
  updateEvaluationCycle,
} from '../../services/peoplePerformanceApi';
import {
  fromLocalDateTimeInputValue,
  toLocalDateTimeInputValue,
} from '../../utils/localDateTimeInput';

const CYCLE_STATUS_LABELS: Record<string, string> = {
  Draft: 'Rascunho',
  Open: 'Aberto',
  Closed: 'Encerrado',
};

function defaultPeriodLocal(): { start: string; end: string } {
  const start = new Date();
  const end = new Date();
  end.setFullYear(end.getFullYear() + 1);
  return {
    start: toLocalDateTimeInputValue(start.toISOString()),
    end: toLocalDateTimeInputValue(end.toISOString()),
  };
}

const initialPeriod = defaultPeriodLocal();

/** Formulário mantém datas no formato datetime-local; converte para ISO no save. */
type CycleFormState = Omit<EvaluationCycleUpsertPayload, 'periodStartUtc' | 'periodEndUtc'> & {
  periodStartLocal: string;
  periodEndLocal: string;
};

const emptyForm: CycleFormState = {
  name: '',
  description: '',
  periodStartLocal: initialPeriod.start,
  periodEndLocal: initialPeriod.end,
  enableSelfEvaluation: true,
  enableManagerEvaluation: true,
  enablePeersEvaluation: false,
  enableUpwardEvaluation: false,
  enableCycleFeedback: true,
  minPeerReviewers: 0,
  maxPeerReviewers: 5,
};

export function EvaluationCyclesPage() {
  const { confirm, confirmDelete } = useMessageBox();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('people.evaluations');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [items, setItems] = useState<EvaluationCycle[]>([]);
  const [form, setForm] = useState<CycleFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const result = await listEvaluationCycles({ pageSize: 100 });
      setItems(result.items);
    } catch {
      toast.error('Falha ao carregar ciclos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  function resetForm(): void {
    const period = defaultPeriodLocal();
    setEditingId(null);
    setForm({ ...emptyForm, periodStartLocal: period.start, periodEndLocal: period.end });
  }

  function startEdit(item: EvaluationCycle): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      description: item.description,
      periodStartLocal: toLocalDateTimeInputValue(item.periodStartUtc),
      periodEndLocal: toLocalDateTimeInputValue(item.periodEndUtc),
      enableSelfEvaluation: item.enableSelfEvaluation,
      enableManagerEvaluation: item.enableManagerEvaluation,
      enablePeersEvaluation: item.enablePeersEvaluation,
      enableUpwardEvaluation: item.enableUpwardEvaluation,
      enableCycleFeedback: item.enableCycleFeedback,
      minPeerReviewers: item.minPeerReviewers,
      maxPeerReviewers: item.maxPeerReviewers,
    });
  }

  function buildCyclePayload(): EvaluationCycleUpsertPayload | null {
    const periodStartUtc = fromLocalDateTimeInputValue(form.periodStartLocal);
    const periodEndUtc = fromLocalDateTimeInputValue(form.periodEndLocal);
    if (!periodStartUtc || !periodEndUtc) {
      toast.error('Informe datas válidas para o período do ciclo.');
      return null;
    }

    return {
      name: form.name,
      description: form.description,
      periodStartUtc,
      periodEndUtc,
      enableSelfEvaluation: form.enableSelfEvaluation,
      enableManagerEvaluation: form.enableManagerEvaluation,
      enablePeersEvaluation: form.enablePeersEvaluation,
      enableUpwardEvaluation: form.enableUpwardEvaluation,
      enableCycleFeedback: form.enableCycleFeedback,
      minPeerReviewers: form.minPeerReviewers,
      maxPeerReviewers: form.maxPeerReviewers,
    };
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const payload = buildCyclePayload();
    if (!payload) return;

    setSaving(true);
    try {
      if (editingId) {
        await updateEvaluationCycle(editingId, payload);
        toast.success('Ciclo atualizado.');
        resetForm();
      } else {
        await createEvaluationCycle(payload);
        toast.success('Ciclo criado. Adicione calendários de avaliação nos detalhes.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao salvar ciclo.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDuplicate(id: string): Promise<void> {
    if (!canWrite) return;
    const item = items.find((current) => current.id === id);
    if (
      !(await confirm({
        title: 'Duplicar ciclo',
        message: item
          ? `Duplicar "${item.name}"? Indicadores e perguntas do ciclo serão clonados.`
          : 'Duplicar este ciclo? Indicadores e perguntas serão clonados.',
        confirmLabel: 'Duplicar',
      }))
    ) {
      return;
    }

    setDuplicatingId(id);
    try {
      await duplicateEvaluationCycle(id);
      toast.success('Ciclo duplicado.');
      await loadData();
    } catch {
      toast.error('Falha ao duplicar ciclo.');
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    const item = items.find((current) => current.id === id);
    const statusLabel = item ? (CYCLE_STATUS_LABELS[item.status] ?? item.status) : null;
    const details: string[] = [];
    if (statusLabel) {
      details.push(`Status atual: ${statusLabel}.`);
    }
    details.push('Esta ação remove o ciclo da listagem.');

    if (
      !(await confirmDelete({
        title: 'Excluir ciclo',
        message: item ? `Excluir o ciclo "${item.name}"?` : 'Excluir este ciclo?',
        details,
      }))
    ) {
      return;
    }

    setDeletingId(id);
    try {
      await deleteEvaluationCycle(id);
      toast.success('Ciclo excluído.');
      if (editingId === id) {
        collapseForm(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao excluir ciclo.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Ciclos de avaliação"
        description="Defina o período de referência do ciclo. As janelas de coleta ficam nos calendários (ex.: meio do ano, fim do ano)."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar ciclo' : 'Novo ciclo'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          isEditing={!!editingId}
          createSummary="Clique para expandir e cadastrar um novo ciclo"
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label md:col-span-2">
                Nome
                <input className="erp-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              </label>
            </div>

            <p className="mt-4 text-sm font-semibold text-secondary-800">Período de referência</p>
            <div className="erp-form-grid mt-2">
              <label className="erp-label">
                Início
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={form.periodStartLocal}
                  onChange={(event) => setForm({ ...form, periodStartLocal: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Fim
                <input
                  type="datetime-local"
                  className="erp-input"
                  value={form.periodEndLocal}
                  onChange={(event) => setForm({ ...form, periodEndLocal: event.target.value })}
                  required
                />
              </label>
            </div>

            <label className="erp-label mt-3 block">
              Descrição
              <textarea className="erp-input mt-1" value={form.description ?? ''} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </label>
            <div className="mt-3 space-y-3">
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={
                    form.enableSelfEvaluation
                    && form.enableManagerEvaluation
                    && form.enablePeersEvaluation
                    && form.enableUpwardEvaluation
                  }
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    setForm({
                      ...form,
                      enableSelfEvaluation: enabled,
                      enableManagerEvaluation: enabled,
                      enablePeersEvaluation: enabled,
                      enableUpwardEvaluation: enabled,
                    });
                  }}
                />
                360° (habilita autoavaliação, gestor, pares e colaborador→gestor)
              </label>
              <div className="grid gap-2 md:grid-cols-2">
                {[
                  ['enableSelfEvaluation', 'Autoavaliação'],
                  ['enableManagerEvaluation', 'Gestor (90°)'],
                  ['enablePeersEvaluation', 'Pares (180°)'],
                  ['enableUpwardEvaluation', 'Colaborador (avalia o gestor)'],
                  ['enableCycleFeedback', 'Feedback do ciclo'],
                ].map(([key, label]) => {
                  const field = key as keyof CycleFormState;
                  return (
                  <label key={key} className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={Boolean(form[field])}
                      onChange={(event) => setForm({ ...form, [field]: event.target.checked })}
                    />
                    {label}
                  </label>
                  );
                })}
              </div>
            </div>
            {form.enablePeersEvaluation ? (
              <div className="erp-form-grid mt-3">
                <label className="erp-label">
                  Mín. revisores (pares)
                  <input type="number" className="erp-input" value={form.minPeerReviewers} onChange={(event) => setForm({ ...form, minPeerReviewers: Number(event.target.value) })} />
                </label>
                <label className="erp-label">
                  Máx. revisores (pares)
                  <input type="number" className="erp-input" value={form.maxPeerReviewers} onChange={(event) => setForm({ ...form, maxPeerReviewers: Number(event.target.value) })} />
                </label>
              </div>
            ) : null}
            <FormActionBar onCancel={() => collapseForm(resetForm)} className="erp-btn-actions mt-4">
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <p className="text-secondary-500">Carregando ciclos...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Status</th>
                <th>Período</th>
                <th>Calendários</th>
                <th className="w-48">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium text-secondary-900">{item.name}</td>
                  <td>
                    <CodedStatusBadge
                      status={item.status}
                      label={CYCLE_STATUS_LABELS[item.status] ?? item.status}
                    />
                  </td>
                  <td className="text-sm text-secondary-600">
                    {new Date(item.periodStartUtc).toLocaleDateString('pt-BR')}
                    {' — '}
                    {new Date(item.periodEndUtc).toLocaleDateString('pt-BR')}
                  </td>
                  <td>{item.calendarCount ?? item.calendars?.length ?? 0}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <ViewIconLink
                        to={`/people/evaluation-cycles/${item.id}`}
                        label="Detalhes do ciclo"
                      />
                      {canWrite && item.status === 'Draft' ? (
                        <EditIconButton label="Editar ciclo" onClick={() => startEdit(item)} />
                      ) : null}
                      {canWrite ? (
                        <DuplicateIconButton
                          label={duplicatingId === item.id ? 'Duplicando...' : 'Duplicar ciclo'}
                          disabled={duplicatingId === item.id || deletingId === item.id}
                          onClick={() => void handleDuplicate(item.id)}
                        />
                      ) : null}
                      {canWrite ? (
                        <DeleteIconButton
                          label={deletingId === item.id ? 'Excluindo...' : 'Excluir ciclo'}
                          disabled={deletingId === item.id || duplicatingId === item.id}
                          onClick={() => void handleDelete(item.id)}
                        />
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
